import React, { useCallback, useContext, useEffect, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import { FetchCallbacksContext } from '../../../internal-components/FetchCallbacks';
import useFetchWithCallbacks from '../../../hooks/useFetchWithCallbacks';
import useTranslations from '../../../hooks/useTranslations';
import { FormContext, useField } from '../FormContext';
import LookupFieldView from '../view/LookupFieldView';
import { FormPreviewContext } from '../../engine/FormPreviewEngine';
import useGlobalVariables from '../../../hooks/useGlobalVariables';

const LookupFieldEngine = ({
  lookupType: givenLookupType,
  internalId,
  value,
  setValue,
  required,
  readOnly,
  title = '',
  lookupInfo = {},
  initialLookupData,
  ...fieldProps // for future development, know whe have this
}) => {
  const formContextProps = useContext(FormContext);
  const formPreviewContextProps = useContext(FormPreviewContext);
  const { formId, getSubmissionsId, onFormChange } = formContextProps;

  const mappedFields =
    formContextProps.mappedFields || formPreviewContextProps.mappedFields;
  const isPublic =
    formContextProps.form && formContextProps.form.sharingType === 'anonymous';

  const t = useTranslations();
  const fetchAPI = useFetchWithCallbacks();
  const { openSnackbar } = useContext(FetchCallbacksContext);

  const [fieldOptions, setFieldOptions] = useState([]);
  const [subFields, setSubFields] = useState([]);
  const [customStatusesById, setCustomStatusesById] = useState({});
  const [subFieldValues, setSubFieldValues] = useState([]);

  let initialSubmissionId, initialUserId, lookupMatches;
  initialSubmissionId = null;
  initialUserId = null;
  if (initialLookupData && initialLookupData.lookupFields && internalId) {
    lookupMatches = initialLookupData.lookupFields.filter(
      one => one.localFieldId === internalId
    );
    if (lookupMatches.length) {
      initialSubmissionId = lookupMatches[0].submissionId || null;
      initialUserId = lookupMatches[0].userId || null;
    }
  }

  const [currentSubmissionId, setCurrentSubmissionId] = useState(null);
  const [submissionId, setSubmissionId] = useState(initialSubmissionId);
  const [userId, setUserId] = useState(initialUserId);
  const [lookupType, setLookupType] = useState(givenLookupType);

  /* Currently checking for submissionId on every render, which is fine because it should actually only need to make an API call maximum once.
   * If this component gets more complex and causes more re-renders, we might want to re-think this.
   */
  getSubmissionsId(false).then(res => {
    if (res) {
      setCurrentSubmissionId(res);
    }
  });

  useEffect(() => {
    const lookupForm = lookupInfo.entityId;
    const lookupSubFields = lookupInfo.subFields;
    let newLookupType = lookupInfo.entityType;

    const initialSubFieldValues = lookupSubFields
      ? lookupSubFields.map(one =>
          typeof one.value === 'undefined' ? undefined : one.value
        )
      : [];

    if (newLookupType === 'user' && formId) {
      let fields = [],
        matchingField = null,
        results = [],
        fieldToPush = null;
      if (mappedFields) {
        fields = cloneDeep(mappedFields);
      }
      lookupSubFields.forEach(lookup => {
        fieldToPush = null;
        if (lookup && lookup.internalId) {
          matchingField = Object.values(fields).find(one => {
            return !!(
              one &&
              one.lookupInfo &&
              one.lookupInfo.subFieldInfo &&
              one.lookupInfo.subFieldInfo.parentFieldId === internalId &&
              one.lookupInfo.subFieldInfo.externalFormFieldId ===
                lookup.internalId
            );
          });
          if (matchingField) {
            fieldToPush = cloneDeep(matchingField);
            fieldToPush.internalId = lookup.internalId;
          }
          if (fieldToPush) {
            results.push({
              ...fieldToPush,
              localFormFieldId: lookup.localFormFieldId
            });
          }
        }
      });
      setSubFields(results);
    } else if (newLookupType === 'form') {
      if (!lookupForm) {
        return;
      }
      let fields = [],
        matchingField = null,
        results = [],
        fieldToPush = null;
      if (mappedFields) {
        fields = cloneDeep(mappedFields);
      }
      lookupSubFields.forEach(lookup => {
        if (lookup && lookup.internalId) {
          fieldToPush = null;
          if (lookup.internalId.indexOf('metadata') !== -1) {
            fieldToPush = {
              internalId: lookup.internalId,
              options: [],
              type: 'number',
              uiType: 'date',
              title: t(`forms.field.lookup.${lookup.internalId}`)
            };
          } else if (lookup.internalId === 'submitterId') {
            fieldToPush = {
              internalId: 'submitterId',
              options: [],
              type: 'text',
              uiType: 'text',
              title: t('forms.field.lookup.submitterId')
            };
          } else if (lookup.internalId.indexOf('statusData') !== -1) {
            fieldToPush = {
              internalId: lookup.internalId,
              options: Object.keys(customStatusesById),
              type: 'text',
              uiType: 'status',
              title: t(`forms.field.lookup.${lookup.internalId}`)
            };
          } else {
            matchingField = Object.values(fields).find(one => {
              return !!(
                one &&
                one.lookupInfo &&
                one.lookupInfo.subFieldInfo &&
                one.lookupInfo.subFieldInfo.externalFormFieldId ===
                  lookup.internalId
              );
            });
            if (matchingField) {
              fieldToPush = cloneDeep(matchingField);
              fieldToPush.internalId = lookup.internalId;
            }
          }
          if (fieldToPush) {
            results.push({
              ...fieldToPush,
              localFormFieldId: lookup.localFormFieldId
            });
          }
        }
      });
      fetchAPI(`/forms/v1/forms/${lookupForm}`).then(res => {
        let newCustomStatusesById = {};
        if (res.customStatuses) {
          res.customStatuses.forEach(one => {
            newCustomStatusesById[one.internalId] = one;
          });
          setCustomStatusesById(newCustomStatusesById);
        }
        setSubFields(results);
      });
    }
    if (
      JSON.stringify(initialSubFieldValues) !== JSON.stringify(subFieldValues)
    ) {
      setSubFieldValues(initialSubFieldValues);
    }
    setLookupType(newLookupType);
  }, [lookupInfo]);

  const validator = useCallback(
    v => {
      v = v ? v : false;
      let error = '';

      if (required && !!!v) {
        error = t('forms.fields.error.required');
      }

      return error;
    },
    [required, t]
  );

  const onChange = newId => {
    let matchingNewId,
      matches,
      matchingOptions,
      matchingInfo,
      newSubFieldValues,
      newValue,
      subFieldValuesById;

    subFieldValuesById = {};
    matches = fieldOptions.filter(one => one.id === newId);
    if (matches && matches.length) {
      matchingNewId = matches[0].id;
    }
    matchingInfo = null;
    matchingOptions = fieldOptions.filter(one => one.id === newId);
    if (matchingOptions && matchingOptions.length) {
      newValue = matchingOptions[0].text;
      matchingInfo =
        matchingOptions[0].info || matchingOptions[0].combinedInputs;
    }
    newSubFieldValues = [];
    if (lookupType === 'user') {
      setUserId(matchingNewId);
    } else {
      setSubmissionId(matchingNewId);
    }
    if (!matchingNewId) {
      setSubFieldValues(newSubFieldValues);
      setValue(newValue);
      return;
    }

    if (lookupType === 'user') {
      newSubFieldValues = [];
      if (!matchingInfo) {
        return;
      }
      subFields.forEach(one => {
        newSubFieldValues.push(matchingInfo[one.internalId] || null);
      });
      setSubFieldValues(newSubFieldValues);
      setValue(matchingInfo.firstName + ' ' + matchingInfo.lastName);
      onFormChange();
    } else {
      newSubFieldValues = [];
      if (!matchingInfo) {
        return;
      }
      matchingInfo[`'metadata'->'createdDate'`] = matchingInfo.createdDate;
      matchingInfo[`'metadata'->'updatedDate'`] = matchingInfo.updatedDate;
      if (customStatusesById && matchingInfo.status) {
        matchingInfo[`'statusData'->'status'`] = `||${matchingInfo.status}||`;
      }
      matchingInfo.submitterId = matchingInfo.firstName
        ? matchingInfo.firstName + ' ' + matchingInfo.lastName
        : '';

      subFields.forEach(one => {
        newSubFieldValues.push(matchingInfo[one.internalId] || null);
        subFieldValuesById[one.localFormFieldId] =
          matchingInfo[one.internalId] || null;
      });
      setSubFieldValues(newSubFieldValues);
      setValue(newValue);
      onFormChange(false, false, subFieldValuesById, internalId);
    }
  };

  const onClickSubField = () => {
    openSnackbar('warning', t('snackbars.field.lookup.read_only'));
  };

  useField(internalId, validator);

  const asyncOptions = debounce(
    (searchValue, currentValues) => {
      return asyncOptionsHelper(searchValue, currentValues);
    },
    1000,
    { leading: true }
  );

  const asyncOptionsHelper = (searchValue = '', currentValues = null) => {
    let lookupField, url;
    url = `/forms/v1${
      isPublic ? '/public' : ''
    }/forms/${formId}/lookup/${internalId}?limit=100`;
    if (searchValue) {
      url += `&searchText=${searchValue}`;
    }
    lookupField = lookupInfo ? lookupInfo.keyField : '';
    if (lookupInfo && lookupType === 'user') {
      lookupField = 'email';
    }
    if (lookupType === 'user' && !searchValue) {
      url = `/forms/v1${
        isPublic ? '/public' : ''
      }/forms/${formId}/lookup/${internalId}?limit=100&searchText=''`;
    }
    if (!lookupField || (lookupType === 'user' && isPublic)) {
      return new Promise((resolve, reject) => resolve([]));
    }
    return fetchAPI(url)
      .then(res => {
        let results, options, oneOption, combinedInputs;
        results = (res && res.results) || [];
        options = [];
        results.forEach(one => {
          if (lookupType === 'user') {
            oneOption =
              one.info.firstName +
              ' ' +
              one.info.lastName +
              ' <' +
              one.info.email +
              '>';
            options.push({
              text: oneOption,
              value: one.id,
              id: one.id,
              info: one.info
            });
          } else {
            combinedInputs = {
              ...one.endUserInput,
              ...one.adminInput,
              ...one.metadata,
              ...one.info,
              ...one.statusData
            };
            if (
              typeof combinedInputs[lookupField] !== 'undefined' &&
              one.id !== currentSubmissionId
            ) {
              oneOption = combinedInputs[lookupField];
              options.push({
                text: oneOption,
                value: one.id,
                id: one.id,
                combinedInputs: combinedInputs
              });
            }
          }
        });
        options.sort((a, b) => {
          let aText, bText;
          aText = (a && a.text) || '';
          bText = (b && b.text) || '';
          if (aText === bText) {
            return 0;
          } else if (aText === currentValues) {
            return -1;
          } else if (bText === currentValues) {
            return 1;
          } else if (typeof aText !== 'string' || typeof bText !== 'string') {
            return aText < bText ? -1 : 1;
          } else return aText.localeCompare(bText);
        });
        options.unshift({
          text: 'forms.field.dropdown.placeholder',
          value: '',
          id: null
        });
        setFieldOptions(options);
        return options;
      })
      .catch(() => {});
  };

  const viewProps = {
    lookupType,
    asyncOptions,
    customStatusesById,
    internalId,
    readOnly,
    fieldOptions,
    subFields,
    submissionId,
    value,
    subFieldValues,
    title,
    required,
    onChange,
    onClickSubField,
    userId
  };

  return <LookupFieldView {...viewProps} />;
};

export default LookupFieldEngine;
